import type { InferContractRouterOutputs } from '@orpc/contract'
import { useInfiniteQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import type { baseContract } from 'pixai-contract/api/orpc'
import { useMemo } from 'react'
import { useMayTokenBeValid } from 'src/utils/token'
import { orpc, orpcPublic } from './contract'
import {
  type OrpcArtworkPreviewFragment,
  toArtworkPreviewFragment,
} from './utils/typeAdapters'

type RecommendArtworksPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getRecommendArtworks']

const getNextPageParam = (lastPage: RecommendArtworksPage) => {
  if (!lastPage.pageInfo.hasNextPage) return undefined
  return lastPage.pageInfo.endCursor
}

/**
 * Build the @orpc/tanstack-query `infiniteOptions` payload that
 * `useOrpcRecommendArtworks` plugs straight into `useInfiniteQuery`.
 *
 * Exported so that the home route's full-SSR entry
 * (`clientRoutes/_app._index.full-ssr.tsx`) can derive the *same*
 * `queryKey` and seed `queryClient.setQueryData(...)` against it
 * before the component mounts. The contract-driven client uses
 * `generateOperationKey(['artwork', 'getRecommendArtworks'], { input, type: 'infinite' })`
 * to shape its key — not the `@hey-api/openapi-ts` codegen path that
 * `artworkGetRecommendArtworksInfiniteQueryKey` lives on — so the
 * two key shapes must be produced from the exact same builder or
 * the SSR seed will sit at a different cache slot than the one the
 * hook reads, the home grid will refetch on first hydrate, and the
 * user will see two different feeds flash.
 */
export function buildRecommendArtworksInfiniteOptions(first: number) {
  return orpc.artwork.getRecommendArtworks.infiniteOptions({
    input: (pageParam: string | undefined) => ({
      first,
      after: pageParam,
    }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
  })
}

/** Visitor-endpoint counterpart for the anonymous recommend feed. */
export function buildRecommendArtworksVisitorInfiniteOptions(first: number) {
  return orpcPublic.artwork.getRecommendArtworksVisitor.infiniteOptions({
    input: (pageParam: string | undefined) => ({
      first,
      after: pageParam,
    }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
  })
}

interface RecommendArtworksResult {
  /**
   * Artworks carry `tackIds: string[]` from the oRPC adapter. `ArtworkPreviewCardSimple`
   * hydrates them into rendered chips per-card via the dedup'd /v2/tack/batch
   * loader (see `useTacksByIds`); the feed hook itself doesn't fetch tacks.
   */
  artworks: OrpcArtworkPreviewFragment[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  feedUuid?: string
  loading: boolean
  loadingMore: boolean
  fetchMore: () => void
}

export function useOrpcRecommendArtworks(
  args: { first: number } | null,
): RecommendArtworksResult {
  // Use the SSR-safe cookie-based hint instead of `useMyInfo().data`:
  //
  // - `useMyInfo()` reads from Apollo's `me` query. On the server it
  //   takes a few hundred ms to resolve (or, more commonly under our
  //   skipToken-on-no-localStorage SSR-safety patch, never resolves
  //   at all), so SSR commits with `isLoggedIn=false` and renders
  //   the visitor recommend feed.
  // - On the first client hydrate commit the Apollo cache is still
  //   empty for the same reason, so `me` is still undefined and
  //   `isLoggedIn` would *also* be false there. But once `useEffect`
  //   fires, Apollo kicks off the real `me` query, it resolves to
  //   the logged-in user, and `isLoggedIn` flips to `true`. The
  //   `result = isLoggedIn ? authResult : visitorResult` ternary
  //   then swaps cache backends mid-flight, and react-query has to
  //   refetch under the *authed* key, replacing every artwork card on
  //   the home grid with a different SSR-vs-CSR set. That's the
  //   visible "first paint shows one grid → grid flips to a totally
  //   different set of artworks" flash on `/<lang>/` for any signed-in
  //   user.
  //
  // `useMayTokenBeValid()` reads from `RequestContextValue.mayTokenBeValid`,
  // which is precomputed in `createRequestContextValue` from the
  // request `Cookie` header on the server and from `document.cookie`
  // on the client. Both reads see the same browser cookie state, so
  // SSR and the first hydrate commit always agree on which endpoint
  // to pick — no mid-flight swap, no second flash. The home loader
  // mirrors the same cookie probe to choose between the visitor and
  // the authed recommend endpoint on the server side, so the data
  // SSR ships and the data the hydrated grid reads from cache are
  // the same set of artworks.
  const isLoggedIn = useMayTokenBeValid()
  const enabled = args != null
  const first = args?.first ?? 0

  const authResult = useInfiniteQuery({
    ...buildRecommendArtworksInfiniteOptions(first),
    enabled: enabled && isLoggedIn,
    // Reset on login/logout: response contains user-dependent fields
    // (liked, prompt visibility) that must not leak across accounts.
    meta: { dependsOn: ['login'] },
  })

  const visitorResult = useInfiniteQuery({
    ...buildRecommendArtworksVisitorInfiniteOptions(first),
    enabled: enabled && !isLoggedIn,
    // Drop visitor cache on login so the authed query takes over cleanly.
    meta: { dependsOn: ['login'] },
  })

  const result = isLoggedIn ? authResult : visitorResult
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    result

  const artworks = useMemo(
    () =>
      uniqBy(
        data?.pages.flatMap(page =>
          page.data.map(item => toArtworkPreviewFragment(item)),
        ) ?? [],
        'id',
      ),
    [data?.pages],
  )

  const lastPage = data?.pages.at(-1)

  return {
    artworks,
    pageInfo: {
      hasNextPage: hasNextPage ?? false,
      endCursor: lastPage?.pageInfo.endCursor,
    },
    feedUuid: data?.pages[0]?.feedUuid,
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    fetchMore: fetchNextPage,
  }
}
